import type { InstanceApi } from '@eis/insuranceservice-sdk';
import { validationIssuesOf } from '@eis/core';

/**
 * Partners on a policy, as the console reads and edits them (SDD-011).
 *
 * # The list is the truth, the roles come from the pin
 *
 * A policy carries `partners[]` — `{roleErn, partnerId}` per party — and the roles it MAY carry
 * come from the version it pins (`GET /v1/policies/{code}/schema`, `partnerRoles`), never from
 * the newest version. Every form here is GENERATED from those rules: one input group per role,
 * as many inputs as the role admits. Nothing about the roles is written down in the console.
 *
 * # What a member never edits
 *
 * The policyholder. On a member it is the master's, resolved at read (decision D7); the pinned
 * roles still list it (P-6), and the form leaves that role out and says why.
 *
 * The only word this file may not use is "version" (SSOT I-6) — the roles come from "the tarif".
 */

export type PartnerRoleRule = InstanceApi.PartnerRoleRule;
export type PartnerLink = InstanceApi.PartnerLink;
export type ResolvedPartner = InstanceApi.ResolvedPartner;

export const POLICYHOLDER_ROLE = 'partnerservice:tag/policyholder';
export const INSURED_PERSON_ROLE = 'partnerservice:tag/insured_person';

/** Form state: per role, the partner ids typed, in order. Blank rows are kept until submit. */
export type PartnerFormValues = Readonly<Record<string, readonly string[]>>;

/** The roles a form offers: every pinned role, minus the policyholder on a member (D7). */
export function editableRoles(
  rules: readonly PartnerRoleRule[] | undefined,
  member: boolean,
): PartnerRoleRule[] {
  return (rules ?? []).filter((rule) => !(member && rule.roleErn === POLICYHOLDER_ROLE));
}

/**
 * Seeds the form from a stored list: the partners already filling each role, padded to the
 * role's minimum with blank rows so a required role shows its input before anything is typed.
 */
export function seedPartnerValues(
  rules: readonly PartnerRoleRule[],
  links: readonly PartnerLink[] | undefined,
): PartnerFormValues {
  const out: Record<string, string[]> = {};
  for (const rule of rules) {
    const roleErn = rule.roleErn ?? '';
    const ids = (links ?? [])
      .filter((link) => link.roleErn === roleErn)
      .map((link) => link.partnerId ?? '');
    const minimum = Math.max(rule.minCount ?? 0, 0);
    while (ids.length < minimum) ids.push('');
    out[roleErn] = ids;
  }
  return out;
}

/** The COMPLETE list a write carries (P-7): role order, row order, blanks dropped. */
export function linksOf(
  rules: readonly PartnerRoleRule[],
  values: PartnerFormValues,
): PartnerLink[] {
  const links: PartnerLink[] = [];
  for (const rule of rules) {
    const roleErn = rule.roleErn ?? '';
    for (const id of values[roleErn] ?? []) {
      const partnerId = id.trim();
      if (partnerId !== '') links.push({ roleErn, partnerId });
    }
  }
  return links;
}

/** How many partners a role admits, in the persona's words: "1 bis 1", "mindestens 1". */
export function roleLabel(rule: PartnerRoleRule): string {
  const name = rule.name ?? '';
  return name !== '' ? name : codeOf(rule.roleErn ?? '');
}

/** `partnerservice:tag/insured_person` → `insured_person`: what to show when no name is known. */
export const codeOf = (roleErn: string): string => roleErn.slice(roleErn.lastIndexOf('/') + 1);

/** The role's display name from the pinned rules, or its code when the rules are not at hand. */
export function roleNameOf(roleErn: string, rules: readonly PartnerRoleRule[] | undefined): string {
  const rule = (rules ?? []).find((candidate) => candidate.roleErn === roleErn);
  return rule === undefined ? codeOf(roleErn) : roleLabel(rule);
}

/** Whether the form may add another row to a role. */
export const canAdd = (rule: PartnerRoleRule, count: number): boolean =>
  rule.maxCount === undefined || count < rule.maxCount;

/** Whether a row may be removed: never below the role's minimum. */
export const canRemove = (rule: PartnerRoleRule, count: number): boolean =>
  count > Math.max(rule.minCount ?? 0, 0);

/**
 * The service's refusal, mapped onto the form (SDD-011 §6).
 *
 * `/partners/<i>/roleErn|partnerId` names the i-th link of the list the form SENT — which is
 * `linksOf` in role and row order — so the index maps back to a role and a row. `/partners`
 * names the list as a whole: a required role unfilled, a role over-filled, with the message
 * naming the role. Both are shown; neither is swallowed into "validation failed".
 */
export interface PartnerRefusal {
  /** Messages about the list as a whole — `min_count`, `max_count`. */
  readonly general: readonly string[];
  /** Messages per row, keyed `${roleErn}#${rowIndex}`. */
  readonly rows: Readonly<Record<string, string>>;
}

export function partnerRefusalOf(
  error: unknown,
  rules: readonly PartnerRoleRule[],
  values: PartnerFormValues,
): PartnerRefusal {
  const issues = validationIssuesOf(error)
    .map((issue) => ({ path: issue.path ?? '', message: issue.message ?? '' }))
    .filter((issue) => issue.path.startsWith('/partners'));
  const general: string[] = [];
  const rows: Record<string, string> = {};
  // The link the service saw at index i is the i-th non-blank row, in role and row order.
  const positions: { roleErn: string; row: number }[] = [];
  for (const rule of rules) {
    const roleErn = rule.roleErn ?? '';
    (values[roleErn] ?? []).forEach((id, row) => {
      if (id.trim() !== '') positions.push({ roleErn, row });
    });
  }
  for (const issue of issues) {
    const match = /^\/partners\/(\d+)(?:\/|$)/.exec(issue.path);
    if (match?.[1] === undefined) {
      general.push(issue.message);
      continue;
    }
    const position = positions[Number(match[1])];
    if (position === undefined) {
      general.push(issue.message);
      continue;
    }
    rows[`${position.roleErn}#${String(position.row)}`] = issue.message;
  }
  return { general, rows };
}

export const hasPartnerRefusal = (refusal: PartnerRefusal): boolean =>
  refusal.general.length > 0 || Object.keys(refusal.rows).length > 0;

/**
 * What to render for a resolved partner: the name when the directory knew one, the id marked
 * unresolved otherwise (the same rule the Group Policy list applies, SSOT §4.2).
 */
export interface PartnerLabel {
  readonly text: string;
  readonly resolved: boolean;
  readonly typeChecked: boolean;
}

export function partnerLabel(partner: ResolvedPartner): PartnerLabel {
  const name = partner.partnerName ?? '';
  const id = partner.link?.partnerId ?? '';
  return {
    text: partner.partnerNameResolved === true && name !== '' ? name : id,
    resolved: partner.partnerNameResolved === true && name !== '',
    typeChecked: partner.partnerTypeChecked === true,
  };
}

/** Groups a segment's resolved partners by role, in first-seen order. */
export function partnersByRole(
  partners: readonly ResolvedPartner[] | undefined,
): { roleErn: string; partners: ResolvedPartner[] }[] {
  const groups = new Map<string, ResolvedPartner[]>();
  for (const partner of partners ?? []) {
    const roleErn = partner.link?.roleErn ?? '';
    const group = groups.get(roleErn);
    if (group === undefined) groups.set(roleErn, [partner]);
    else group.push(partner);
  }
  return [...groups.entries()].map(([roleErn, list]) => ({ roleErn, partners: list }));
}

/** Whether two segments carry different parties — the timeline's "Beteiligte geändert" marker. */
export function partnersDiffer(
  a: readonly ResolvedPartner[] | undefined,
  b: readonly ResolvedPartner[] | undefined,
): boolean {
  const key = (list: readonly ResolvedPartner[] | undefined): string =>
    (list ?? [])
      .map((partner) => `${partner.link?.roleErn ?? ''}|${partner.link?.partnerId ?? ''}`)
      .sort()
      .join(',');
  return key(a) !== key(b);
}

/** The list detachment writes (R-7): the insured person fills the policyholder role, the rest stays. */
export function promotedPartners(policy: InstanceApi.Policy | undefined): PartnerLink[] {
  const links = (policy?.partners ?? [])
    .map((partner) => partner.link)
    .filter((link): link is PartnerLink => link !== undefined)
    .filter((link) => link.roleErn !== POLICYHOLDER_ROLE);
  const insured = policy?.insuredPerson?.partnerId ?? '';
  return insured === '' ? links : [...links, { roleErn: POLICYHOLDER_ROLE, partnerId: insured }];
}
